import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 80vh;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.primaryText};
  text-align: center;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${props => props.theme.secondaryText};
  text-align: center;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  transition: color 0.3s ease;
`;

const Section = styled.section`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 2rem;
  text-align: center;
  transition: color 0.3s ease;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const FeatureCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.3s ease;
  border: 1px solid ${props => props.theme.border};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadowHover};
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.theme.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const FeatureDescription = styled.p`
  color: ${props => props.theme.secondaryText};
  line-height: 1.6;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const ComparisonSection = styled.div`
  background: ${props => props.theme.secondaryBg};
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 3rem;
  transition: background-color 0.3s ease;
`;

const ComparisonTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1.5rem;
  text-align: center;
  transition: color 0.3s ease;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ComparisonColumn = styled.div`
  text-align: center;
`;

const ColumnTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const ComparisonList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ComparisonItem = styled.li`
  padding: 0.75rem 0;
  color: ${props => props.theme.secondaryText};
  border-bottom: 1px solid ${props => props.theme.border};
  transition: color 0.3s ease, border-color 0.3s ease;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CtaSection = styled.div`
  text-align: center;
  background: ${props => props.theme.cardBg};
  border-radius: 12px;
  padding: 3rem 2rem;
  box-shadow: ${props => props.theme.shadow};
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
`;

const CtaTitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const CtaText = styled.p`
  font-size: 1.125rem;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  transition: color 0.3s ease;
`;

const CtaButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primaryHover};
  }
`;

const WhyUsPage = () => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  usePageTitle('Why Slick Picks – Explainable MLB Betting Model');

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <Container theme={theme}>
      <PageMeta description="See why Slick Picks is different from typical betting services, with transparent MLB player prop models, SHAP explainability, and a focus on long-term edges over hype." />
      <Title theme={theme}>Why Choose SlickPicks?</Title>
      <Subtitle theme={theme}>
        Discover what makes us different from the competition and why our data-driven approach 
        gives you the edge in sports predictions.
      </Subtitle>

      <Section>
        <SectionTitle theme={theme}>Our Key Differentiators</SectionTitle>
        <FeaturesGrid>

        <FeatureCard theme={theme}>
            <FeatureIcon theme={theme}>💎</FeatureIcon>
            <FeatureTitle theme={theme}>Transparency for Trust</FeatureTitle>
            <FeatureDescription theme={theme}>
              We show our winnings and we show our losses. Numbers don't lie. We share insights for our models and what is driving our predictions.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard theme={theme}>
            <FeatureIcon theme={theme}>🏟️</FeatureIcon>
            <FeatureTitle theme={theme}>MLB Baseball Ops Experience</FeatureTitle>
            <FeatureDescription theme={theme}>
              Our lead engineer has previously worked in Baseball Operations for the Baltimore Orioles, bridging both scouting and analytics departments. We know how to measure talent. 
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard theme={theme}>
            <FeatureIcon theme={theme}>🔍</FeatureIcon>
            <FeatureTitle theme={theme}>MLB Scout Development Program Graduate</FeatureTitle>
            <FeatureDescription theme={theme}>
              Our lead engineer was sponsored to attend the MLB Scout Development Program where he was taught how to evaluate talent and project performance by those who do it for a living.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard theme={theme}>
            <FeatureIcon theme={theme}>⚾</FeatureIcon>
            <FeatureTitle theme={theme}>Former Division I Baseball Player</FeatureTitle>
            <FeatureDescription theme={theme}>
              Our lead engineer was a four year starter for a Division I baseball team. He knows what a good matchup looks like from personal experience.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard theme={theme}>
            <FeatureIcon theme={theme}>🎓</FeatureIcon>
            <FeatureTitle theme={theme}>MIT Applied Data Science Graduate</FeatureTitle>
            <FeatureDescription theme={theme}>
              Our lead engineer graduated from MIT's Applied Data Science program where he learned how to build machine learning models to make accurate predictions.
            </FeatureDescription>
          </FeatureCard>


          <FeatureCard theme={theme}>
            <FeatureIcon theme={theme}>🙋‍♂️</FeatureIcon>
            <FeatureTitle theme={theme}>Built for our own use</FeatureTitle>
            <FeatureDescription theme={theme}>
              We use our own models to make predictions for our own use. We're not trying to sell you a product, we're trying to share our edge.
            </FeatureDescription>
          </FeatureCard>


        </FeaturesGrid>
      </Section>

      <Section>
        <CtaSection theme={theme}>
          <CtaTitle theme={theme}>Ready to Get Started?</CtaTitle>
          <CtaText theme={theme}>
            Take the guessing game out of the equation and rely upon proven, data-driven predictions, built by a credible source.
          </CtaText>
          <CtaButton theme={theme} onClick={handleGetStarted}>
            Start Winning Today
          </CtaButton>
        </CtaSection>
      </Section>
    </Container>
  );
};

export default WhyUsPage;
